import type { LoyaltyRedemptionContext } from './types';

/**
 * Partial loyalty redemption at the till.
 *
 * The wallet tender used to be all-or-nothing: the cashier picked WALLET and
 * the server derived the points needed to settle the whole bill. FR-CRM-005
 * and FR-CRM-023 ask for a partial redemption with a per-role cap, and
 * `LoyaltyRedemptionInput` has existed in this package, unmounted, since the
 * Tier-4 batch.
 *
 * A partial redemption is expressed as a SPLIT with a WALLET leg, because that
 * path already exists and is already validated: `assertWalletCovers` checks the
 * customer's balance and the tenant's redemption floor against the LEG amount,
 * not the bill. So nothing here is trusted — this is what the cashier sees
 * while choosing, and the server decides.
 *
 * Kept out of the component so the arithmetic is testable without a DOM, the
 * same reason `split-payment.ts` sits beside it.
 */

/**
 * The most points worth spending on this bill.
 *
 * Whichever runs out first: the customer's balance, or the points it takes to
 * clear the bill. Redeeming past the total would owe the customer change in
 * cash for points, which no tender here does.
 *
 * The bill-side ceiling rounds UP, matching `LoyaltyService.pointsNeededFor`
 * server-side. A floor would leave a balance that genuinely covers the bill one
 * point short of being allowed to.
 */
export function maxRedeemablePoints(
    loyalty: LoyaltyRedemptionContext | undefined,
    billTotalMinor: number,
): number {
    if (!loyalty || billTotalMinor <= 0) return 0;
    // Guard the divisor: a misconfigured 0 would otherwise give Infinity and
    // let the slider run to the customer's whole balance on a 1-paise bill.
    const perPoint = Math.max(1, loyalty.pointValuePaise);
    const enoughForBill = Math.ceil(billTotalMinor / perPoint);
    return Math.max(0, Math.min(loyalty.availablePoints, enoughForBill));
}

/**
 * What `points` are worth, in minor units.
 *
 * Points x value, with no divisor — the value is ALREADY minor units. The
 * component got this wrong once (`/100` on top), which announced every
 * redemption at a hundredth of its worth; the same mistake on this side would
 * under-charge the wallet leg instead.
 */
export function redemptionAmount(
    loyalty: LoyaltyRedemptionContext | undefined,
    points: number,
): number {
    if (!loyalty || points <= 0) return 0;
    return points * loyalty.pointValuePaise;
}

/** Does this redemption settle the whole bill, or is a second tender needed? */
export function redemptionCoversBill(
    loyalty: LoyaltyRedemptionContext | undefined,
    points: number,
    billTotalMinor: number,
): boolean {
    return redemptionAmount(loyalty, points) >= billTotalMinor;
}

/**
 * Is this redemption one the server will accept?
 *
 * Mirrors `assertWalletCovers`: the balance must cover it and it must reach the
 * tenant's floor. A `minimumPoints` of 0 means no floor, which is what an
 * INACTIVE programme reports. This is advisory — the server re-checks — but a
 * cashier should not be able to press Confirm into a guaranteed rejection.
 */
export function isRedeemable(
    loyalty: LoyaltyRedemptionContext | undefined,
    points: number,
): boolean {
    if (!loyalty || points <= 0) return false;
    if (points > loyalty.availablePoints) return false;
    return loyalty.minimumPoints <= 0 || points >= loyalty.minimumPoints;
}
